/**
 * The catalogue tile — one event, space or piece of equipment, in the brand
 * reference's card anatomy (`.superpowers/plans/brand-reference/style.css`,
 * `.card`; matched in Task 4, 1 October 2026).
 *
 * Three parts, always in this order, so two tiles side by side line up:
 *
 * 1. **`TileArt`** — 160px (175px from `sm`): the item's photograph, or, when it
 *    has none, the reference's colour block for its kind with the kind's line
 *    icon (owner ruling: *"photo, else block"*). A text chip at the top start
 *    names the kind, so the block is never the only signal of what the item is.
 * 2. **`TileBody`** — 22px padding: a 12px muted meta line, the serif title,
 *    an optional muted description (clamped to two lines), then
 * 3. **its foot** — one hairline-separated row: the value (price or date) at
 *    the start and the wine "View details" at the end.
 *
 * ⚠️ **The foot is pinned to the bottom of the tile (`mt-auto`), and the tile
 * fills its grid cell (`h-full`).** That is what puts every hairline in a row at
 * the same height whatever the title length: the grid stretches each cell to
 * the row's tallest tile, and the foot rides the bottom edge of each. The one
 * thing that can still break it is a foot that wraps, which is why its value
 * may shrink (`min-w-0`) and the affordance may not (`shrink-0`). The event
 * tile's date is the compact `formatEventCardWhen` form in two `nowrap`
 * halves, so where a tile is too narrow for one line it breaks between date
 * and time, never mid-zone ("…9:00 AM Egypt / Time" at 390, Task 4 review M-1,
 * finish review item 6). ⚠️ That makes an event's foot two lines on a narrow
 * tile while a listing's price stays one — and in the homepage's mixed "All"
 * grid (three-up at 1024px, ~290px tiles) the event's hairline sat ~21px above
 * its neighbours'. So under `reserveDescription` the foot holds two lines
 * whenever the tile is narrower than one line of date needs (see `TileBody`).
 *
 * The lift on hover is the reference's −4px, and **only under
 * `motion-safe:`** — a card that moves under the pointer is motion the reader
 * did not ask for.
 *
 * No text is hardcoded (rule 1): the tag, meta, title, foot and affordance all
 * arrive as props. The link is the caller's (`react-router`'s `Link`), passed
 * as `children` of `Tile`, because `shared/ui` does not know the routes.
 */
import { cn } from './cn'

export type TileKind = 'event' | 'space' | 'equipment'

export type TileProps = React.ComponentPropsWithoutRef<'div'>

/** The card frame: hairline, 7px radius, no shadow, and the hover lift. */
export function Tile({ className, ...props }: TileProps) {
  return (
    <div
      className={cn(
        'group h-full overflow-hidden rounded-[7px] border border-line bg-raised',
        'transition-[border-color,translate] duration-200 hover:border-line-strong',
        'motion-safe:focus-within:-translate-y-1 motion-safe:hover:-translate-y-1',
        className,
      )}
      {...props}
    />
  )
}

/**
 * The block behind a photo-less tile, per kind. Events are the wine fill; the
 * other two are their own palette tokens (`--ns-art-*`), defined in both
 * schemes. Each pairs a ground with the ink its icon is drawn in.
 */
const ART_CLASS: Record<TileKind, string> = {
  event: 'bg-brand-600 text-fg-onbrand',
  space: 'bg-art-space text-art-space-fg',
  equipment: 'bg-art-equipment text-art-equipment-fg',
}

export type TileArtProps = {
  kind: TileKind
  /** The item's own photograph. `null`/absent draws the kind's block instead. */
  imageUrl?: string | null | undefined
  /** The chips at the top start — the kind's name, and any flag ("Free"). */
  children?: React.ReactNode
}

export function TileArt({ kind, imageUrl, children }: TileArtProps) {
  return (
    <div className="relative h-40 shrink-0 sm:h-[175px]">
      {imageUrl ? (
        <img src={imageUrl} alt="" loading="lazy" className="size-full object-cover" />
      ) : (
        <div className={cn('flex size-full items-center justify-center', ART_CLASS[kind])}>
          <KindIcon kind={kind} />
        </div>
      )}
      {children ? (
        <div className="absolute start-4 top-4 flex flex-wrap gap-1.5">{children}</div>
      ) : null}
    </div>
  )
}

/**
 * A chip on the art — the reference's `.tag`: 11px, white at ~92%, 3px radius.
 * `raised` rather than white so it follows dark mode; ink on it measures as body
 * text does on a card. `className` takes a tone (`text-success` for "Free").
 */
export function TileTag({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      className={cn('rounded-[3px] bg-raised/92 px-2 py-0.5 text-[11px] text-fg', className)}
      {...props}
    />
  )
}

export type TileBodyProps = {
  meta: React.ReactNode
  title: React.ReactNode
  /** A second, muted line under the title. Omitted when the item has none —
   *  never filled with invented copy. */
  description?: React.ReactNode
  /** Keep ONE line of the description's slot even when there is none, so a
   *  page of the homepage's `PagedGrid` whose tiles have no description is
   *  not a whole slot shorter than the next. Only the homepage needs it; a
   *  browse page is not paged and must not carry a blank gap. */
  reserveDescription?: boolean
  /** The foot's start: the price or the date. */
  value: React.ReactNode
  /** The foot's end: "View details". */
  action: React.ReactNode
}

export function TileBody({
  meta,
  title,
  description,
  reserveDescription = false,
  value,
  action,
}: TileBodyProps) {
  return (
    <div className="@container flex flex-1 flex-col p-[22px]">
      <p dir="auto" className="mb-2.5 truncate text-xs leading-[1.6] text-fg-muted">
        {meta}
      </p>
      {/* Serif 24px at the reference's normal tracking — `font-display`
          resolves to the Arabic face under `:lang(ar)`. Clamped to two lines
          so one long title cannot push its row's feet down by a paragraph. */}
      <h3
        dir="auto"
        className="line-clamp-2 font-display text-2xl leading-[1.2] font-normal text-fg"
      >
        {title}
      </h3>
      {/* ⚠️ **The slot is reserved only when `reserveDescription` is set** —
          and then only **one line** of it (homepage finish review, 1 October
          2026). Task 6 reserved the reference's two lines
          (`.card-description { min-height: 45px }`) because the homepage grid
          turns a page every five seconds and hidden pages take no layout, so a
          page with a description and a page without were different heights
          and the bands below jumped. Once cards carried real excerpts, the
          reviewer saw the other side of it: every description-less card had a
          two-line hole under its title. One line is the compromise the owner's
          controller ruled — a venue line's height — so a page turn moves the
          bands by at most one line and an empty card never reads as missing a
          paragraph. `min-h-[1lh]` is one line of *this* text (14px at 1.6),
          not a magic pixel count. Browse pages are not paged and their
          hairlines already align through `mt-auto`, so there it is no slot at
          all. The 14px above and below are the reference's paragraph margins. */}
      {reserveDescription ? (
        <div className="my-3.5 min-h-[1lh] text-sm leading-[1.6]">
          {description ? (
            <p dir="auto" className="line-clamp-2 text-fg-muted">
              {description}
            </p>
          ) : null}
        </div>
      ) : description ? (
        <p dir="auto" className="mt-3.5 line-clamp-2 text-sm leading-[1.6] text-fg-muted">
          {description}
        </p>
      ) : null}
      {/* `mt-auto` on the wrapper, not on the row: the auto margin takes
          whatever the row's tallest tile leaves over, so every hairline in a
          row sits at the same height. */}
      <div className={cn('mt-auto', !reserveDescription && 'pt-3.5')}>
        {/* ⚠️ **Two lines held on a narrow homepage tile** (finish review,
            1 October 2026). An event's date breaks between date and time when
            the tile is too narrow for one line — measured, the longest
            same-year date, its gap and "View details" need 303px of this
            container (19rem = 304px) — and its foot grows a line a listing
            price's does not. In a mixed row the hairlines then disagree. Below
            that width every homepage tile's foot is two lines tall (1px rule
            + 15px + 2 × 13px at 1.6), so they agree again. A container query,
            not a breakpoint: a 390px phone's single tile is wide enough, a
            1024px laptop's three-up tile is not. Browse pages (no
            `reserveDescription`) are untouched. */}
        <div
          className={cn(
            'flex items-center justify-between gap-3 border-t border-line pt-[15px] text-[13px] leading-[1.6]',
            reserveDescription && '@max-[19rem]:min-h-[calc(16px+2lh)]',
          )}
        >
          <span className="min-w-0 text-fg">{value}</span>
          <span className="shrink-0 font-bold text-brand-fg group-hover:underline">{action}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * The reference's three line icons (`app.js`, `icons`), verbatim, on its
 * 96-unit grid at a 3-unit stroke. `currentColor`, so the block's ink colours
 * them; decorative, because the tag beside them already says the kind in words.
 */
function KindIcon({ kind }: { kind: TileKind }) {
  return (
    <svg
      viewBox="0 0 96 96"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      aria-hidden="true"
      focusable="false"
      className="size-[90px] opacity-85"
    >
      {kind === 'event' ? (
        <>
          <rect x="16" y="20" width="64" height="60" rx="6" />
          <path d="M16 38h64M32 12v17M64 12v17M32 52h10M54 52h10M32 65h10" />
        </>
      ) : kind === 'space' ? (
        <path d="M15 82V28l34-15 34 15v54M15 34l34 15 34-15M49 49v33M29 42v40M69 42v40" />
      ) : (
        <>
          <rect x="12" y="30" width="57" height="44" rx="8" />
          <path d="m69 41 17-10v41L69 62M29 30l8-14h18l8 14" />
          <circle cx="42" cy="52" r="13" />
        </>
      )}
    </svg>
  )
}
